import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Plus, PencilSimple, Trash, Image as ImageIcon, GoogleLogo, Quotes, ArrowSquareOut, Eye, EyeSlash, Upload } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const TYPE_META = {
  testimonial: { label: "Kundenstimme", icon: <Quotes size={14} weight="bold" /> },
  google_review: { label: "Google-Rezension", icon: <GoogleLogo size={14} weight="bold" /> },
  before_after: { label: "Vorher / Nachher", icon: <ImageIcon size={14} weight="bold" /> },
};

export default function ReferencesPanel({ tenant }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [newType, setNewType] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/references"); setRows(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.type === filter);
  const counts = {
    all: rows.length,
    testimonial: rows.filter((r) => r.type === "testimonial").length,
    google_review: rows.filter((r) => r.type === "google_review").length,
    before_after: rows.filter((r) => r.type === "before_after").length,
  };

  const del = async (id) => {
    if (!window.confirm("Referenz wirklich löschen?")) return;
    await api.delete(`/references/${id}`);
    await load();
  };
  const toggleActive = async (r) => {
    await api.patch(`/references/${r.reference_id}`, { active: !r.active });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="references-panel">
      <section className="bg-gradient-to-br from-amber-50 to-white border border-black/10 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
              <Star size={18} weight="fill" className="text-amber-500" /> Referenzen &amp; Bewertungen
            </h3>
            <p className="text-xs text-zinc-500 mt-1">Vertrauen schaffen: Kundenstimmen, Vorher/Nachher-Fotos und Google-Rezensionen erscheinen unter <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">/portal/{tenant?.slug}/referenzen</code>.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => { setNewType("testimonial"); setEditRow({ type: "testimonial", rating: 5, active: true }); }} className="rounded-none h-9 bg-black text-white hover:bg-zinc-800" data-testid="new-testimonial-btn">
              <Plus size={14} weight="bold" className="mr-1" /> Kundenstimme
            </Button>
            <Button size="sm" onClick={() => { setNewType("google_review"); setEditRow({ type: "google_review", rating: 5, active: true }); }} className="rounded-none h-9 bg-black text-white hover:bg-zinc-800" data-testid="new-google-btn">
              <GoogleLogo size={14} weight="bold" className="mr-1" /> Google-Rezension
            </Button>
            <Button size="sm" onClick={() => { setNewType("before_after"); setEditRow({ type: "before_after", active: true }); }} className="rounded-none h-9 bg-black text-white hover:bg-zinc-800" data-testid="new-beforeafter-btn">
              <ImageIcon size={14} weight="bold" className="mr-1" /> Vorher / Nachher
            </Button>
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: "Alle" },
          { k: "testimonial", label: "Kundenstimmen" },
          { k: "google_review", label: "Google" },
          { k: "before_after", label: "Vorher / Nachher" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.k ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-600 hover:bg-zinc-50"}`}
            data-testid={`filter-${f.k}`}
          >
            {f.label} <span className="opacity-60">({counts[f.k]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8 text-zinc-400 text-sm">Lade …</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-black/15 p-10 text-center">
          <ImageIcon size={32} weight="bold" className="mx-auto text-zinc-300 mb-3" />
          <div className="text-sm text-zinc-500">Noch keine Referenzen — klicke oben, um zu starten.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <ReferenceCard key={r.reference_id} row={r} onEdit={() => { setNewType(r.type); setEditRow(r); }} onDelete={() => del(r.reference_id)} onToggle={() => toggleActive(r)} />
          ))}
        </div>
      )}

      <ReferenceEditor
        open={!!editRow}
        row={editRow}
        type={newType}
        onClose={() => { setEditRow(null); setNewType(null); }}
        onSaved={async () => { setEditRow(null); setNewType(null); await load(); }}
      />
    </div>
  );
}

function ReferenceCard({ row, onEdit, onDelete, onToggle }) {
  const meta = TYPE_META[row.type] || {};
  const isPhoto = row.type === "before_after";
  return (
    <div className={`bg-white border p-4 relative group transition ${row.active ? "border-black/10" : "border-black/10 opacity-60"}`} data-testid={`reference-card-${row.reference_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          {meta.icon} {meta.label}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggle} className="p-1 hover:bg-zinc-100 rounded" title={row.active ? "Ausblenden" : "Einblenden"} data-testid={`toggle-${row.reference_id}`}>
            {row.active ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" className="text-zinc-400" />}
          </button>
          <button onClick={onEdit} className="p-1 hover:bg-zinc-100 rounded" title="Bearbeiten" data-testid={`edit-${row.reference_id}`}>
            <PencilSimple size={14} weight="bold" />
          </button>
          <button onClick={onDelete} className="p-1 hover:bg-red-50 text-red-600 rounded" title="Löschen" data-testid={`delete-${row.reference_id}`}>
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>
      {isPhoto ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <img src={resolveUrl(row.photo_before_url)} alt="Vorher" className="w-full h-32 object-cover bg-zinc-100" onError={(e) => { e.target.style.background="#fee"; e.target.alt="Bild fehlt"; }} />
            <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-widest">Vorher</span>
          </div>
          <div className="relative">
            <img src={resolveUrl(row.photo_after_url)} alt="Nachher" className="w-full h-32 object-cover bg-zinc-100" onError={(e) => { e.target.style.background="#efe"; e.target.alt="Bild fehlt"; }} />
            <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-widest">Nachher</span>
          </div>
          {row.caption && <div className="col-span-2 text-xs text-zinc-600 mt-1">{row.caption}</div>}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-0.5 mb-1.5" aria-label={`${row.rating} von 5 Sternen`}>
            {[1,2,3,4,5].map((n) => (
              <Star key={n} size={12} weight={n <= (row.rating || 0) ? "fill" : "regular"} className={n <= (row.rating || 0) ? "text-amber-400" : "text-zinc-300"} />
            ))}
          </div>
          <p className="text-sm text-zinc-700 leading-relaxed line-clamp-3">{row.text}</p>
          <div className="mt-2 text-xs text-zinc-500 flex items-center gap-2">
            <strong className="text-zinc-800">{row.author_name}</strong>
            {row.author_location && <span>· {row.author_location}</span>}
            {row.date_iso && <span>· {row.date_iso}</span>}
            {row.source_url && (
              <a href={row.source_url} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 text-blue-600 hover:underline">
                <ArrowSquareOut size={11} weight="bold" /> Quelle
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ReferenceEditor({ open, row, type, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const fileBeforeRef = useRef(null);
  const fileAfterRef = useRef(null);
  const fileAvatarRef = useRef(null);

  useEffect(() => {
    setForm(row || {});
    setErr(null);
  }, [row]);

  if (!open) return null;
  const isEdit = !!row?.reference_id;
  const effectiveType = type || row?.type;

  const upload = async (file, field) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/references/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: r.data.url }));
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      if (isEdit) {
        await api.patch(`/references/${row.reference_id}`, form);
      } else {
        await api.post("/references", { ...form, type: effectiveType });
      }
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const meta = TYPE_META[effectiveType] || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="reference-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{meta.icon} {isEdit ? "Bearbeiten" : "Neu"}: {meta.label}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {effectiveType === "before_after"
              ? "Lade Vorher- und Nachher-Fotos hoch, um den sichtbaren Unterschied zu zeigen."
              : "Pflege eine Rezension oder Kundenstimme. Nur aktive Einträge werden öffentlich angezeigt."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {effectiveType === "before_after" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <PhotoField label="Vorher-Foto" url={form.photo_before_url} onFile={(f) => upload(f, "photo_before_url")} fileRef={fileBeforeRef} testid="upload-before" />
                <PhotoField label="Nachher-Foto" url={form.photo_after_url} onFile={(f) => upload(f, "photo_after_url")} fileRef={fileAfterRef} testid="upload-after" />
              </div>
              <div>
                <Label className="text-xs">Bildunterschrift (optional)</Label>
                <Input value={form.caption || ""} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="z.B. Restmüll 240L, 6 Wochen ohne Reinigung" data-testid="ref-caption" />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Autor / Kunde *</Label>
                  <Input value={form.author_name || ""} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Familie Müller" data-testid="ref-author" />
                </div>
                <div>
                  <Label className="text-xs">Ort (optional)</Label>
                  <Input value={form.author_location || ""} onChange={(e) => setForm({ ...form, author_location: e.target.value })} placeholder="Haltingen" data-testid="ref-location" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Bewertung: {form.rating || 5} ★</Label>
                <div className="flex items-center gap-1 mt-1">
                  {[1,2,3,4,5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-1 hover:scale-110 transition"
                      data-testid={`ref-star-${n}`}
                    >
                      <Star size={22} weight={n <= (form.rating || 0) ? "fill" : "regular"} className={n <= (form.rating || 0) ? "text-amber-400" : "text-zinc-300"} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Text der Rezension *</Label>
                <Textarea rows={4} value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Ich bin sehr zufrieden…" data-testid="ref-text" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Datum (optional)</Label>
                  <Input type="date" value={form.date_iso || ""} onChange={(e) => setForm({ ...form, date_iso: e.target.value })} data-testid="ref-date" />
                </div>
                <div>
                  <Label className="text-xs">Avatar-Bild (optional)</Label>
                  <div className="flex items-center gap-2">
                    <input ref={fileAvatarRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], "avatar_url")} />
                    <Button type="button" size="sm" variant="outline" className="rounded-none h-9" onClick={() => fileAvatarRef.current?.click()} data-testid="upload-avatar"><Upload size={12} weight="bold" className="mr-1" /> Hochladen</Button>
                    {form.avatar_url && <img src={resolveUrl(form.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover" />}
                  </div>
                </div>
              </div>
              {effectiveType === "google_review" && (
                <div>
                  <Label className="text-xs">Google Maps Link (optional)</Label>
                  <Input value={form.source_url || ""} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://g.co/kgs/…" data-testid="ref-source-url" />
                  <p className="text-[10px] text-zinc-400 mt-1">Kopiere den Link zur echten Rezension aus deinem Google-Business-Profil, damit Besucher sie verifizieren können.</p>
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sortier-Position</Label>
              <Input type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: +e.target.value })} data-testid="ref-sort" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="ref-active">
                <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Öffentlich sichtbar
              </label>
            </div>
          </div>
          {err && <div className="text-xs text-red-600" data-testid="ref-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Abbrechen</Button>
            <Button className="rounded-none bg-black text-white hover:bg-zinc-800" onClick={save} disabled={saving} data-testid="ref-save">
              {saving ? "Speichere…" : "Speichern"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PhotoField({ label, url, onFile, fileRef, testid }) {
  return (
    <div>
      <Label className="text-xs">{label} *</Label>
      <div className="border border-black/10 aspect-square bg-zinc-50 relative overflow-hidden">
        {url ? (
          <img src={resolveUrl(url)} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 text-xs">
            <ImageIcon size={28} weight="regular" className="mb-1" />
            Kein Bild
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="absolute bottom-2 right-2 bg-black/80 hover:bg-black text-white text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1"
          data-testid={testid}
        >
          <Upload size={11} weight="bold" /> {url ? "Ersetzen" : "Hochladen"}
        </button>
      </div>
    </div>
  );
}
